import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MailPlus, Ticket, XIcon } from "lucide-react";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import useProfessionals, {
  SupervisorOption,
} from "@/features/professionals/hooks/use-professionals";
import { ProfessionalCard } from "@/features/professionals/ui/professional-card";
import VaulDrawer from "@/components/ui/drawer";
import AddEditProfessionalsForm from "@/features/professionals/ui/professional-form";
import ProfessionalFiltersOnly from "@/features/professionals/ui/professional-filters-only";
import { SearchBar } from "@/components/common/search-bar";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import useAllProfessionals from "@/features/professionals/hooks/use-all-professional";
import BulkInviteDialog from "@/features/professionals/ui/bulk-invite-dialog";
import { Button } from "@/components/ui/button";
import { ContentLayout } from "@/components/common/content-layout";

// Stable while the list hasn't loaded, so the filter bar's memo and effect
// don't re-run on a fresh [] every render.
const NO_SUPERVISORS: SupervisorOption[] = [];

export default function Professionals() {
  const [searchParams] = useSearchParams();
  const [showProfessionalFormDrawer, setShowProfessionalFormDrawer] =
    useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  // The drawer is how a professional is VIEWED, not only edited, so it must
  // open for everyone who can see the list. Only what it lets you do depends
  // on Edit.
  const canWrite = useHasAccess("write");
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  // The page comes from the URL, so every writer of `page=0` (the filters,
  // Reset, the search bar) also resets the request, not only the pager.
  const _page = parseInt(searchParams.get("page") || "0", 10);
  const navigate = useNavigate();

  // Get filter values from URL params
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || undefined;
  const job_commitment = searchParams.get("job_commitment") || undefined;
  const type = searchParams.get("type") || undefined;
  const qualification =
    searchParams.get("qualification")?.split(",") || undefined;
  const is_supervisor = searchParams.get("is_supervisor") || undefined;
  const is_lead = searchParams.get("is_lead") || undefined;
  const supervisor_id = searchParams.get("supervisor_id") || undefined;

  // Fetch professionals with filters
  const { data, error, isFetching, isLoading } = useProfessionals(
    _page,
    search,
    status,
    job_commitment,
    qualification,
    type,
    is_supervisor,
    is_lead,
    supervisor_id
  );

  // The paged list only ever holds one screen of professionals. The bulk
  // dialog has to offer the whole roster, so it reads the unpaginated query.
  const { data: allProfessionals, isLoading: isLoadingAll } = useAllProfessionals();

  const professionals = data?.professionals || [];
  const supervisors = data?.supervisors ?? NO_SUPERVISORS;
  const leads = data?.leads || [];
  const currentPage = data?.currentPage || 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  useEffect(() => {
    if (selectedProfessional) {
      const updated = professionals.find((p) => p.id === selectedProfessional.id);
      if (updated) setSelectedProfessional(updated);
    }
  }, [professionals]);

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < lastPage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", page.toString());
      navigate(`?${newParams.toString()}`, { replace: true });
    }
  };

  return (
    <ContentLayout
      header={
        <div className="flex flex-wrap items-center gap-4">
          <ProfessionalFiltersOnly
            supervisors={supervisors}
            supervisorsLoaded={!!data}
          />
          <div className="flex-1 min-w-[200px]">
            <SearchBar
              placeholder="Search Professionals..."
              urlParamStrategy="prefix"
              clearButton={{
                enabled: true,
                position: "inside",
                ariaLabel: "Clear search",
                icon: <XIcon className="w-4 h-4" />
              }}
              // fieldSelector={{
              //   enabled: true,
              //   fields: [
              //     { value: "name", label: "Name" },
              //     { value: "email", label: "Email" },
              //     { value: "phone", label: "Phone" }
              //   ],
              //   allowAll: true
              // }}
            />
          </div>
          <HasAccess permission="write">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowBulkInvite(true)}
            >
              <MailPlus className="mr-2 h-4 w-4" />
              Invite to Dashboard
            </Button>
          </HasAccess>
        </div>
      }
      data={professionals}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      errorMessage="Failed to load professionals"
      emptyState={{
        message: "No professionals found matching your criteria",
        icon: <Ticket className="h-8 w-8 text-muted-foreground" />
      }}
    >
      <ProfessionalCard
        professionals={professionals}
        setShowProfessionalDrawer={setShowProfessionalFormDrawer}
        setSelectedProfessional={setSelectedProfessional}
      />
      {professionals.length > 0 && (
        <CustomPaginationComponent
          currentPage={currentPage}
          lastPage={lastPage}
          onPageChange={handlePageChange}
        />
      )}
      <BulkInviteDialog
        open={showBulkInvite}
        onOpenChange={setShowBulkInvite}
        professionals={allProfessionals.professionals}
        isLoading={isLoadingAll}
      />
      <VaulDrawer
        drawerDirection="right"
        isOpen={showProfessionalFormDrawer}
        onOpenChange={setShowProfessionalFormDrawer}
        triggerChildren={null}
        contentChildren={
          <AddEditProfessionalsForm
            readOnly={!canWrite}
            editMode={!!selectedProfessional}
            onClose={() => setShowProfessionalFormDrawer(false)}
            initialData={selectedProfessional}
            supervisors={supervisors}
            leads={leads}
          />
        }
      />
    </ContentLayout>
  );
}
